import React, { useEffect, useState } from "react";
import {
  getWarehouses,
  getSuppliers,
  getPurchaseOrders,
  getPurchaseOrderById,
  createPurchaseOrder,
} from "../services/procurementApi";
import "./ProcurementPage.css";

const emptyLot = () => ({
  supplier_id: "",
  lot_identifier: "",
  pricing_type: "per_piece",
  total_cost: "",
  weight_kg: "",
  piece_count: "",
  target_category: "",
});

const ProcurementPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [poNumber, setPoNumber] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [notes, setNotes] = useState("");
  const [lots, setLots] = useState([emptyLot()]);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Expandable PO detail
  const [expandedPo, setExpandedPo] = useState(null); // id
  const [poDetail, setPoDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadAll = async () => {
    try {
      const [wh, sup, pos] = await Promise.all([getWarehouses(), getSuppliers(), getPurchaseOrders()]);
      setWarehouses(wh);
      setSuppliers(sup);
      setPurchaseOrders(pos);
    } catch (err) {
      setMsg({ type: "error", text: "Failed to load procurement data." });
    }
  };

  useEffect(() => { loadAll(); }, []);

  const togglePoDetail = async (poId) => {
    if (expandedPo === poId) { setExpandedPo(null); setPoDetail(null); return; }
    setExpandedPo(poId);
    setDetailLoading(true);
    try {
      const detail = await getPurchaseOrderById(poId);
      setPoDetail(detail);
    } catch (err) {
      setMsg({ type: "error", text: "Failed to load PO details." });
      setExpandedPo(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const updateLot = (index, field, value) => {
    setLots((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const previewUnitCost = (lot) => {
    const total = Number(lot.total_cost);
    const pieces = Number(lot.piece_count);
    if (!total || !pieces) return null;
    return (total / pieces).toFixed(2);
  };

  const addLot = () => setLots((prev) => [...prev, emptyLot()]);
  const removeLot = (index) => setLots((prev) => prev.filter((_, i) => i !== index));

  const handleCreatePO = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!poNumber || !warehouseId || lots.length === 0) {
      setMsg({ type: "error", text: "PO number, warehouse and at least one lot are required." });
      return;
    }
    const payload = {
      po_number: poNumber,
      warehouse_id: warehouseId,
      notes,
      lots: lots.map((l) => ({
        ...l,
        total_cost: Number(l.total_cost),
        piece_count: Number(l.piece_count),
        weight_kg: l.pricing_type === "per_kg" ? Number(l.weight_kg) : undefined,
      })),
    };
    setLoading(true);
    try {
      const po = await createPurchaseOrder(payload);
      setMsg({ type: "success", text: `PO ${po.po_number} created — total cost $${po.total_cost}, ${po.lots.length} lot(s). Scan the physical pieces in next (Smart Scan), then allocate them under "Warehouse Allocation".` });
      setPoNumber(""); setWarehouseId(""); setNotes(""); setLots([emptyLot()]);
      loadAll();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to create purchase order." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="procurement-page">
      <h1>Procurement — PO Intake</h1>
      <p className="subtitle">
        Raise a purchase order with per-piece or per-kg bulk lots, against a warehouse you already set up
        (Warehouse Setup page). Suppliers are your existing team members with the "Seller" role (add one via
        User Management) — not created here.
        Unit cost is calculated automatically (average-cost flag set for kg lots). Individual pieces still
        get their SKU through your existing Smart Scan flow — this just tracks the bulk purchase &amp; cost.
      </p>

      {msg && <div className={`proc-msg ${msg.type}`}>{msg.text}</div>}

      {suppliers.length === 0 && (
        <div className="proc-msg error" style={{ marginBottom: 12 }}>
          No Seller-role users found yet. Add a team member with role "Seller" in User Management to use
          them as a supplier here.
        </div>
      )}
      {warehouses.length === 0 && (
        <div className="proc-msg error" style={{ marginBottom: 12 }}>
          No warehouse found yet. Go to Warehouse Setup (Step 1) and create one first.
        </div>
      )}

      <div className="proc-section">
        <h2><span className="proc-step-badge">PO</span> New Purchase Order (bulk lot)</h2>
        <form onSubmit={handleCreatePO}>
          <div className="proc-grid" style={{ marginBottom: 16 }}>
            <div className="proc-field">
              <label>PO Number</label>
              <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO-2026-10-092" />
            </div>
            <div className="proc-field">
              <label>Warehouse</label>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">Select warehouse</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
              </select>
            </div>
            <div className="proc-field">
              <label>Notes</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
            </div>
          </div>

          {lots.map((lot, i) => (
            <div className="proc-lot-card" key={i}>
              {lots.length > 1 && <button type="button" className="remove-lot" onClick={() => removeLot(i)}>Remove</button>}
              <div className="proc-grid">
                <div className="proc-field">
                  <label>Supplier</label>
                  <select value={lot.supplier_id} onChange={(e) => updateLot(i, "supplier_id", e.target.value)}>
                    <option value="">Select supplier (seller)</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.email ? ` — ${s.email}` : ""}</option>)}
                  </select>
                </div>
                <div className="proc-field">
                  <label>Lot identifier</label>
                  <input value={lot.lot_identifier} onChange={(e) => updateLot(i, "lot_identifier", e.target.value)} placeholder="LOT-EU-SNEAKER-01" />
                </div>
                <div className="proc-field">
                  <label>Pricing type</label>
                  <select value={lot.pricing_type} onChange={(e) => updateLot(i, "pricing_type", e.target.value)}>
                    <option value="per_piece">Per piece</option>
                    <option value="per_kg">Per kg (bulk)</option>
                  </select>
                </div>
                <div className="proc-field">
                  <label>Target category (note)</label>
                  <input value={lot.target_category} onChange={(e) => updateLot(i, "target_category", e.target.value)} placeholder="shoes / tops / bottoms…" />
                </div>
                <div className="proc-field">
                  <label>Total cost ($)</label>
                  <input type="number" step="0.01" value={lot.total_cost} onChange={(e) => updateLot(i, "total_cost", e.target.value)} />
                </div>
                {lot.pricing_type === "per_kg" && (
                  <div className="proc-field">
                    <label>Weight (kg)</label>
                    <input type="number" step="0.001" value={lot.weight_kg} onChange={(e) => updateLot(i, "weight_kg", e.target.value)} />
                  </div>
                )}
                <div className="proc-field">
                  <label>Piece count</label>
                  <input type="number" value={lot.piece_count} onChange={(e) => updateLot(i, "piece_count", e.target.value)} />
                </div>
                <div className="proc-field">
                  <label>Unit cost (preview)</label>
                  <div className="proc-unit-cost">
                    {previewUnitCost(lot) ? `$${previewUnitCost(lot)}${lot.pricing_type === "per_kg" ? " (avg)" : ""}` : "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button type="button" className="proc-btn secondary" onClick={addLot}>+ Add another lot</button>
            <button type="submit" className="proc-btn primary" disabled={loading}>{loading ? "Creating…" : "Create Purchase Order"}</button>
          </div>
        </form>
      </div>

      <div className="proc-section">
        <h2>Recent Purchase Orders <span className="proc-count-pill">{purchaseOrders.length}</span></h2>
        <table className="proc-table">
          <thead><tr><th>PO Number</th><th>Warehouse</th><th>Status</th><th>Total Cost</th><th>Created</th><th>Notes</th></tr></thead>
          <tbody>
            {purchaseOrders.map((po) => (
              <React.Fragment key={po.id}>
                <tr
                  onClick={() => togglePoDetail(po.id)}
                  style={{ cursor: "pointer", background: expandedPo === po.id ? "#f0f9ff" : undefined }}
                >
                  <td className="proc-unit-cost">
                    <span style={{ marginRight: 6, fontSize: 10, color: "#94a3b8" }}>{expandedPo === po.id ? "▼" : "▶"}</span>
                    {po.po_number}
                  </td>
                  <td>{po.warehouse_code}</td>
                  <td><span className={`status-chip status-${String(po.status || "").toLowerCase()}`}>{po.status}</span></td>
                  <td>${Number(po.total_cost).toFixed(2)}</td>
                  <td>{new Date(po.created_at).toLocaleDateString()}</td>
                  <td style={{ color: "#64748b", fontSize: 12 }}>{po.notes || "—"}</td>
                </tr>
                {expandedPo === po.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: "#f8fafc" }}>
                      {detailLoading ? (
                        <div style={{ padding: 16, color: "#94a3b8", textAlign: "center" }}>Loading lots…</div>
                      ) : poDetail?.lots?.length > 0 ? (
                        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "#e2e8f0" }}>
                              <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569" }}>Lot ID</th>
                              <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569" }}>Supplier</th>
                              <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569" }}>Pricing</th>
                              <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>Pieces</th>
                              <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>Weight (kg)</th>
                              <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>Total Cost</th>
                              <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>Unit Cost</th>
                              <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569" }}>Category</th>
                            </tr>
                          </thead>
                          <tbody>
                            {poDetail.lots.map((lot) => (
                              <tr key={lot.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                <td style={{ padding: "6px 10px", fontWeight: 600, color: "#0f172a" }}>{lot.lot_identifier}</td>
                                <td style={{ padding: "6px 10px" }}>{lot.supplier_name}{lot.supplier_email ? ` (${lot.supplier_email})` : ""}</td>
                                <td style={{ padding: "6px 10px" }}>
                                  <span className={`status-chip ${lot.pricing_type === "per_kg" ? "status-picked" : "status-stored"}`}>
                                    {lot.pricing_type === "per_kg" ? "Per kg" : "Per piece"}
                                  </span>
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "right" }}>{lot.piece_count}</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", color: "#64748b" }}>{lot.weight_kg || "—"}</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>${Number(lot.total_cost).toFixed(2)}</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#2563eb" }}>${Number(lot.calculated_unit_cost).toFixed(2)}{lot.is_average_cost ? " (avg)" : ""}</td>
                                <td style={{ padding: "6px 10px" }}>{lot.target_category || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ padding: 16, color: "#94a3b8", textAlign: "center" }}>No lots found for this PO.</div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {purchaseOrders.length === 0 && <tr><td colSpan={6} style={{ color: "#9ca3af" }}>No purchase orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProcurementPage;
